import { type CSSProperties, type JSX, useEffect } from 'react';

import classNames from 'classnames';

import { getBackgroundColorStyle } from '../utils/background';
import { computeColor } from '../utils/colors';
import { computeFont } from '../utils/fonts';
import { getFontSizeVariables } from '../utils/getFontSizeVariables';

import { textColors, textColorsFallback } from '../assets/colors';
import { AnimatedText } from './AnimatedText';
import { InlineLinkElement } from './InlineLink';
import * as styles from './Text.css';
import type { LeafMarkProps, Mark, TextLeafProps } from './Text.types';
import { TimestampLinkElement } from './TimestampLink';

export function BoldTextLeaf(props: LeafMarkProps) {
  const { attributes, children } = props;
  return <strong {...attributes}>{children}</strong>;
}

export function ItalicTextLeaf(props: LeafMarkProps) {
  const { attributes, children } = props;
  return <em {...attributes}>{children}</em>;
}

export function UnderlineTextLeaf(props: LeafMarkProps) {
  const { leaf, attributes, children } = props;
  const color = computeColor(leaf.color, textColorsFallback);
  return (
    <u
      style={{
        textDecoration: 'underline',
        textDecorationColor: color,
      }}
      {...attributes}
    >
      {children}
    </u>
  );
}

export function StrikethroughTextLeaf(props: LeafMarkProps) {
  const { attributes, children, mark } = props;
  const attrs = mark.attrs || {};

  const style: CSSProperties = {};

  if (attrs.strikethroughColor) {
    style.color = attrs.strikethroughColor;
  }

  if (attrs.strikethroughFontSize) {
    style.fontSize = attrs.strikethroughFontSize;
  }

  if (attrs.strikethroughFontWeight) {
    style.fontWeight = attrs.strikethroughFontWeight;
  }

  return (
    <s style={style} {...attributes}>
      {children}
    </s>
  );
}

export function CodeTextLeaf(props: LeafMarkProps) {
  const { attributes, children } = props;
  return <code {...attributes}>{children}</code>;
}

export function SubscriptTextLeaf(props: LeafMarkProps) {
  const { attributes, children } = props;
  return <sub {...attributes}>{children}</sub>;
}

export function SuperscriptTextLeaf(props: LeafMarkProps) {
  const { attributes, children } = props;
  return <sup {...attributes}>{children}</sup>;
}

export function HighlightTextLeaf(props: LeafMarkProps) {
  const { attributes, children, mark } = props;

  const style = {
    backgroundColor: mark.attrs?.color,
    color: 'inherit',
  } as CSSProperties;

  return (
    <mark data-color={mark.attrs?.color} style={style} {...attributes}>
      {children}
    </mark>
  );
}

export function UnknownTextLeaf(props: LeafMarkProps) {
  const { children } = props;

  useEffect(() => {
    // Silently error out
    console.warn('UNKNOWN TextLeaf, Is there misimplementation here?');
  }, []);

  return <span>{children}</span>;
}

export function StyledTextLeaf(props: LeafMarkProps) {
  const { attributes, children, mark } = props;
  const attrs = mark.attrs || {};

  const lineHeight = attrs.lineHeight || undefined;
  let style: CSSProperties = {};

  const fontSize = attrs.fontSize || undefined;

  const mobileFontSize = attrs.mobileFontSize || undefined;
  const mobileLineHeight = attrs.mobileLineHeight || undefined;

  style = {
    ...getBackgroundColorStyle(attrs.background),
    fontFamily: computeFont(attrs?.fontFamily),
    fontWeight: attrs.fontWeight || undefined,
    letterSpacing: attrs.letterSpacing || undefined,
    '--line-height': lineHeight,
    '--mobile-line-height': mobileLineHeight,
    '--color': computeColor(attrs?.color || mark.attrs?.color, textColorsFallback),
    '--text-color-hover': attrs?.hoverTextColor || undefined,
    '--text-decoration-hover': attrs?.hoverDecorationLine || undefined,
    '--text-decoration-color-hover': attrs?.hoverDecorationColor || undefined,
    ...getFontSizeVariables(fontSize, mobileFontSize),
  } as unknown as CSSProperties;

  const styledTextStyle: CSSProperties = attrs.fontGradient
    ? {
        backgroundImage: attrs.fontGradient,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        WebkitBoxDecorationBreak: 'clone',
        backgroundRepeat: 'no-repeat',
      }
    : {};

  return (
    <span
      style={{
        ...style,
        ...styledTextStyle,
      }}
      {...attributes}
      className={styles.styledLeaf}
    >
      {children}
    </span>
  );
}

const markToComponent = {
  textStyle: StyledTextLeaf,
  highlight: HighlightTextLeaf,
  italic: ItalicTextLeaf,
  code: CodeTextLeaf,
  bold: BoldTextLeaf,
  underline: UnderlineTextLeaf,
  strike: StrikethroughTextLeaf,
  link: InlineLinkElement,
  timestampLink: TimestampLinkElement,
  subscript: SubscriptTextLeaf,
  superscript: SuperscriptTextLeaf,
};

export function TextLeaf(props: TextLeafProps) {
  const { className, attributes, children } = props;
  return (
    <span className={classNames(className, styles.text)} {...attributes}>
      {children}
    </span>
  );
}

// This component is only used in renderer via serializer
// Editor directly use components above via plugins
export function SerializeTextLeaf(props: TextLeafProps) {
  const { leaf, isClientSideNavigation, onClick } = props;

  const textToRender = leaf.animation ? (
    <AnimatedText
      animation={
        leaf.animation
          ? {
              ...leaf.animation,
              words: [leaf.text, ...leaf.animation.words],
            }
          : undefined
      }
      style={
        leaf.marks?.[0].attrs?.gradient && leaf.color
          ? {
              backgroundImage: textColors[leaf.color as keyof typeof textColors],
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              WebkitBoxDecorationBreak: 'clone',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    />
  ) : (
    leaf.text
  );

  const renderMarks = (marks: Mark[]): JSX.Element => {
    if (marks.length === 0) return <>{textToRender}</>;

    const currentMarkType = marks[0].type;

    const Component = markToComponent[currentMarkType as keyof typeof markToComponent] || UnknownTextLeaf;

    return (
      <Component mark={marks[0]} leaf={leaf} isClientSideNavigation={isClientSideNavigation} onClick={onClick}>
        {renderMarks(marks.slice(1))}
      </Component>
    );
  };

  return <TextLeaf {...props}>{renderMarks(leaf.marks || [])}</TextLeaf>;
}
